import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Paper,
  Typography,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  AutoAwesome,
  Lightbulb,
  Close,
  CheckCircle,
  Description
} from '@mui/icons-material';
import aiService from '../../services/aiService';

const AIInspectionReportField = ({ 
  value = '', 
  onChange, 
  onReportGenerated,
  inspectionType = 'general',
  projectContext = {},
  verificationRequest = {},
  required = false 
}) => {
  const [showAIOptions, setShowAIOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedInspectionType, setSelectedInspectionType] = useState(inspectionType);
  const [internalValue, setInternalValue] = useState(value);
  
  // Use internal state if onChange is not provided
  const currentValue = onChange ? value : internalValue;
  const handleChange = onChange || setInternalValue;

  const inspectionTypes = [
    { value: 'material_delivery', label: 'Material Delivery Verification' },
    { value: 'foundation', label: 'Foundation Inspection' },
    { value: 'structural', label: 'Structural Inspection' },
    { value: 'electrical', label: 'Electrical Inspection' },
    { value: 'plumbing', label: 'Plumbing Inspection' },
    { value: 'roofing', label: 'Roofing Inspection' },
    { value: 'final', label: 'Final Inspection' },
    { value: 'progress', label: 'Progress Check' },
    { value: 'general', label: 'General Verification' }
  ];

  const handleGenerateReport = async () => {
    // Use current text as prompt, or generate from scratch if empty
    const userPrompt = currentValue.trim() || 'Generate a professional inspection report';
    
    setLoading(true);
    try {
      const response = await aiService.generateInspectionReport({
        inspectionType: selectedInspectionType,
        userPrompt: userPrompt,
        projectContext: projectContext || {},
        verificationRequest: verificationRequest || {}
      });

      console.log('AI Inspection Report Response:', response);

      if (response?.report) {
        if (onChange) {
          onChange(response.report);
        } else {
          setInternalValue(response.report);
        }
        // Call onReportGenerated if provided
        if (onReportGenerated) {
          onReportGenerated(response);
        }
      }

      if (response?.suggestions && Array.isArray(response.suggestions)) {
        setSuggestions(response.suggestions);
      }
    } catch (error) {
      console.error('Error generating inspection report:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Append suggestion to existing report
    const newReport = currentValue 
      ? `${currentValue}\n\n${suggestion}`
      : suggestion;
    if (onChange) {
      onChange(newReport);
    } else {
      setInternalValue(newReport);
    }
  };

  const handleImproveReport = async () => {
    if (!currentValue.trim()) return;

    setLoading(true);
    try {
      const response = await aiService.improveInspectionReport({
        report: currentValue,
        inspectionType: selectedInspectionType,
        projectContext,
        verificationRequest
      });

      console.log('AI Improve Report Response:', response);

      if (response?.improved) {
        if (onChange) {
          onChange(response.improved);
        } else {
          setInternalValue(response.improved);
        }
      }
    } catch (error) {
      console.error('Error improving inspection report:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          Inspection Report {required && '*'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Generate or improve report with AI">
            <Button
              size="small"
              startIcon={loading ? <CircularProgress size={16} /> : <AutoAwesome />}
              onClick={currentValue.trim() ? handleImproveReport : handleGenerateReport}
              disabled={loading}
              variant="outlined"
              color="primary"
            >
              {loading 
                ? (currentValue.trim() ? 'Improving...' : 'Generating...') 
                : (currentValue.trim() ? 'Improve with AI' : 'Generate with AI')
              }
            </Button>
          </Tooltip>
          <Tooltip title={showAIOptions ? "Hide AI Options" : "Show AI Options"}>
            <IconButton
              size="small"
              onClick={() => setShowAIOptions(!showAIOptions)}
              color={showAIOptions ? "primary" : "default"}
            >
              <AutoAwesome fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={12}
        value={currentValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Write your inspection report here, or type brief notes and click 'Generate with AI' to create a professional report..."
        required={required}
        disabled={loading}
      />

      <Collapse in={showAIOptions}>
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
            AI Options:
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Inspection Type</InputLabel>
            <Select
              value={selectedInspectionType}
              onChange={(e) => setSelectedInspectionType(e.target.value)}
              label="Inspection Type"
            >
              {inspectionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {suggestions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Lightbulb fontSize="small" />
                Suggestions (click to add):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    size="small"
                    variant="outlined"
                    color="primary"
                    icon={<CheckCircle />}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

export default AIInspectionReportField;
